import { FC, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AssessmentDocumentViewModel } from '@features/assessment/types/assessment-workflow-view-model.types';
import { Box, Paper, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { TooltipCard } from '@components';
import { DI_TOKENS } from '@constants';
import {
  AssessmentDocumentChangeBadge,
  DomainDetails,
  DomainDetailsSkeleton,
  DomainNavigation,
  DomainValidationBanner,
  getIsAnyFileDeleting,
  getIsAnyFileUploading,
  getIsDomainEditable,
  getSaveBlockReason,
  getUploadedDocumentsCount,
  useAssessmentExitFlow,
  useAssessmentFileOperations,
  useAssessmentWorkflowViewModels,
  useBrowserBackGuard,
  useDomainEditor,
  useDomainNavigation,
  useEditPromptTooltip,
  useGetAssessmentDomainDetails,
  useNavigationGuard,
} from '@features/assessment';
import { useDocumentClassification } from '@features/assessment/hooks/useDocumentClassification';
import { findDomainByOrderIndex } from '@features/assessment/utils/assessment-mapper';
import {
  computeDomainProfileFieldNeeds,
  DomainFieldNeedsBanner,
  useActiveTemplateWithValues,
} from '@features/tenant-profile-hub';
import { useInjection } from '@hooks';
import {
  AssessmentDomainStatus,
  DocumentClassificationInvalidReason,
  DocumentClassificationStatus,
  LanguageCode,
} from '@libs/constants';
import { Trans, useTrans } from '@libs/i18n';
import { FileUploadType } from '@libs/types';
import { CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { AssessmentWorkflowStore } from '@stores';

/**
 * Assessment Workflow Page
 *
 * Renders per-domain content within the AssessmentLayout. The layout owns the
 * re-assessment banner, the assessment header (name + Exit button), and the
 * ProgressFlow stepper. This page contributes:
 * - DomainDetails (the current domain's evidence card)
 * - DomainNavigation (back / save & next / etc.)
 * - SaveChangesModal, ClassificationWarningModal, ExitAssessmentModal
 *
 * Cross-cutting state (stepper disabled flag, step-click guard, exit guard)
 * is published into AssessmentWorkflowStore for the layout to consume.
 *
 * @see {@link CLAUDE.md} - Assessment workflow architecture
 */
export const AssessmentWorkflowPage: FC = observer(function AssessmentWorkflowPage() {
  const { assessmentId, domainId } = useParams<{
    assessmentId: string;
    domainId?: string;
  }>();

  // Early validation - assessmentId is required.
  //
  // The guard sits in this thin wrapper, above a component boundary, so the
  // page body can never grow hooks underneath an early return. Bailing out here
  // swaps which component type is rendered, so React unmounts the body outright.
  // Bailing out *inside* the body would instead leave the same component mounted
  // with a different hook count than the render before it — React error #310,
  // "Rendered more hooks than during the previous render".
  if (!assessmentId) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Typography variant="body1" color="error">
          <Trans
            i18nKey={'app.assessments.error.noAssessmentId'}
            defaults="Assessment ID not found"
          />
        </Typography>
      </Box>
    );
  }

  return (
    <AssessmentWorkflowPageContent
      assessmentId={assessmentId}
      domainIdentifier={domainId || ''}
    />
  );
});

interface AssessmentWorkflowPageContentProps {
  /** Non-optional by construction: the wrapper above bails out when it is missing. */
  assessmentId: string;
  domainIdentifier: string;
}

/**
 * The page body. Every hook here runs unconditionally — the only early returns
 * are the loading and domain-not-found branches near the end, with no hook
 * after them.
 */
const AssessmentWorkflowPageContent: FC<AssessmentWorkflowPageContentProps> = observer(function AssessmentWorkflowPageContent({
  assessmentId,
  domainIdentifier,
}: AssessmentWorkflowPageContentProps) {
  const { t } = useTrans();

  const workflowStore = useInjection<AssessmentWorkflowStore>(DI_TOKENS.AssessmentWorkflowStore);

  // ============================================================================
  // API QUERIES & MUTATIONS
  // ============================================================================

  // The assessment + previousAssessmentSummary are fetched once by AssessmentLayout
  // (and exposed through `workflowStore.assessment`); only request what's truly
  // per-domain here.
  const {
    data: domainDetailsData,
    isLoading: isLoadingDomainDetails,
    isFetching,
  } = useGetAssessmentDomainDetails(
    {
      assessmentId,
      assessmentDomainIdentifier: domainIdentifier || '',
    },
    {
      includeAssessment: false,
      includePreviousDomainSummary: true,
    },
  );

  const assessment = workflowStore.assessment ?? undefined;

  const {
    domainDetailsViewModel,
    progressFlowViewModel,
    previousVersionViewModel,
    documents,
    expectedDocuments,
    selectedIndex,
  } = useAssessmentWorkflowViewModels({ domainDetailsData, domainIdentifier });

  const { parentVersion, parentDomainScorePercentage, preloadedDocumentCount } =
    previousVersionViewModel;

  // Per-domain Organization Profile needs: which org fields this domain
  // consumes are still missing, surfaced as a nudge above the domain card.
  // Derived client-side from the active template (each field carries the
  // standard documents it feeds) intersected with this domain's documents — no
  // dedicated endpoint required.
  const { data: activeTemplate } = useActiveTemplateWithValues();
  const currentDomainKey = domainDetailsData?.assessmentDomain?.frameworkDomain?.domainKey;
  const domainStandardDocumentKeys = useMemo(
    () =>
      Array.from(
        new Set(
          (domainDetailsViewModel?.fields ?? [])
            .flatMap((field) => field.documentRequirements ?? [])
            .map((requirement) => requirement.key)
            .filter(Boolean),
        ),
      ),
    [domainDetailsViewModel],
  );
  const domainProfileNeeds =
    activeTemplate && currentDomainKey
      ? computeDomainProfileFieldNeeds({
          domainKey: currentDomainKey,
          standardDocumentKeys: domainStandardDocumentKeys,
          template: activeTemplate,
        })
      : undefined;

  // ============================================================================
  // FILE OPERATIONS HOOK
  // ============================================================================

  /**
   * File operations hook - provides:
   * - files: Consolidated Map of all files (uploaded + uploading + pending)
   * - handleAddFiles: Upload new files with optimistic UI
   * - handleDeleteFile: Delete uploaded or pending files
   * - handleCancelUpload: Cancel in-progress uploads
   */
  const {
    filesArray,
    deletedFiles,
    handleAddFiles,
    handleDeleteFile,
    handleUndoDelete,
    handleRetryDelete,
    handleRetryUpload,
    handleRemoveFailedUpload,
    handleCancelUpload,
    markFileAsMoved,
  } = useAssessmentFileOperations(
    assessmentId,
    domainDetailsViewModel?.maxRequiredDocuments || 0,
    domainDetailsViewModel?.minRequiredDocuments || 0,
    domainDetailsViewModel?.id || '',
    documents,
    domainDetailsViewModel?.status,
  );

  // ============================================================================
  // EDITOR STATE + NAVIGATION GUARD + DOMAIN NAVIGATION + EXIT FLOW
  // ============================================================================

  // "Click Edit to start" tooltip — shown on hover of the Edit button or
  // when the user clicks any disabled control on a completed domain.
  const editPromptTooltip = useEditPromptTooltip();

  // Per-domain editor state (noEvidence / applicable / validationError) and
  // the `needsToSave` predicate. Resets on every domain change.
  const {
    noEvidenceChecked,
    setNoEvidenceChecked,
    isApplicable,
    setIsApplicable,
    validationError,
    setValidationError,
    needsToSave,
  } = useDomainEditor({
    domainDetailsViewModel,
    progressFlowViewModel,
    files: filesArray,
    deletedFiles,
  });

  // Unsaved-changes guard: the SaveChangesModal is rendered by the layout
  // and reads its open flag/handlers straight from the store. We only consume
  // the in-page primitives (guardedNavigate / hasUnsavedChanges / isProcessing).
  const navigationGuard = useNavigationGuard({
    assessmentId,
    currentDomain: domainDetailsViewModel,
    files: filesArray,
    deletedFiles,
    noEvidenceChecked,
    isApplicable,
  });
  const {
    isProcessing: isNavigationProcessing,
    hasUnsavedChanges,
    guardedNavigate,
  } = navigationGuard;

  // All inter-domain navigation handlers + the shared save/start mutations.
  const {
    handleBack,
    handleSaveAndNext,
    handleNodeClick,
    handleResume,
    handleResumeLater,
    handleNext,
    handleEditDomain,
    navigateToPreviousDomainWithStart,
    saveDomainMutation,
    startDomainMutation,
  } = useDomainNavigation({
    assessmentId,
    assessment,
    domainDetailsViewModel,
    progressFlowViewModel,
    selectedIndex,
    files: filesArray,
    noEvidenceChecked,
    isApplicable,
    setValidationError,
    navigationGuard,
  });

  // Exit-modal flow: registers the modal's handlers in the store and exposes
  // `handleExit` for the layout's Exit button (via `registerExitGuard` below).
  // Shares the save mutation with `useDomainNavigation` so "Save & Exit" and
  // "Save & Next" report the same `isPending`.
  const { handleExit, cancelDomainMutation, deleteAssessmentMutation } = useAssessmentExitFlow({
    assessmentId,
    assessment,
    domainDetailsViewModel,
    progressFlowViewModel,
    files: filesArray,
    noEvidenceChecked,
    isApplicable,
    needsToSave,
    saveDomainMutation,
  });

  const isMutating =
    saveDomainMutation.isPending ||
    startDomainMutation.isPending ||
    cancelDomainMutation.isPending ||
    deleteAssessmentMutation.isPending;

  const isLoading = isLoadingDomainDetails;
  const isCurrentDomainCompleted =
    domainDetailsViewModel?.status === AssessmentDomainStatus.COMPLETED;

  // ============================================================================
  // DOCUMENT CLASSIFICATION HOOK
  // ============================================================================

  /**
   * Document classification hook - provides:
   * - classifyDocument: Trigger classification for a document
   * - retryClassification: Retry failed classification
   * - isClassifying: Check if document is being classified
   * - getClassificationError: Get error message for failed classification
   */
  const {
    renderClassificationBadge,
    isDocumentMoving,
    isDocumentProcessing,
    classificationIssues,
    handleSaveWithClassificationCheck,
    isAnyClassifying,
  } = useDocumentClassification({
    assessmentId,
    assessmentDomainId: domainDetailsViewModel?.id,
    language: assessment?.language?.code as LanguageCode | undefined,
    filesArray,
    onDocumentMoved: markFileAsMoved,
    onSaveAndNext: handleSaveAndNext,
  });

  // ============================================================================
  // BROWSER BACK BUTTON HANDLER
  // ============================================================================

  const navigate = useNavigate();

  /**
   * True while any file is queued or actively uploading.
   * Blocks ALL navigation until uploads settle (success or failure).
   */
  const isAnyFileUploading = useMemo(() => getIsAnyFileUploading(filesArray), [filesArray]);

  /**
   * True while any file has a pending-delete countdown or is actively being deleted.
   * Blocks ALL navigation until the deletion settles.
   */
  const isAnyFileDeleting = useMemo(() => getIsAnyFileDeleting(filesArray), [filesArray]);

  /**
   * Browser back button handling. The hook owns the `popstate` lifecycle
   * and decides between hard-block (uploads in flight), soft-prompt
   * (unsaved changes → SaveChangesModal), and pass-through.
   */
  useBrowserBackGuard({
    isBlocked: isAnyFileUploading,
    hasUnsavedChanges,
    guardedNavigate,
    onIntercepted: navigateToPreviousDomainWithStart,
  });

  /**
   * Check if domain is currently in progress (editable)
   */
  const isDomainInProgress = getIsDomainEditable(domainDetailsData?.assessmentDomain?.status);
  /**
   * Actions disabled state
   * Disabled when:
   * - Domain is NOT in progress (completed or not started)
   * - Mutation is pending
   * - Navigation guard processing (save/discard modal actions)
   */
  const isDomainFieldsDisabled =
    !isDomainInProgress || isMutating || isNavigationProcessing || isLoading || isFetching;

  /**
   * Navigation disabled state
   * Disabled during:
   * - Domain save operation
   * - Domain start operation
   * - Navigation guard processing (save/discard modal actions)
   * - Any file upload in progress
   */
  const isNavigationDisabled =
    isMutating ||
    isNavigationProcessing ||
    isLoading ||
    isFetching ||
    isAnyFileUploading ||
    isAnyFileDeleting;

  // ============================================================================
  // PUBLISH STATE + GUARDS INTO THE WORKFLOW STORE FOR THE LAYOUT TO CONSUME
  // ============================================================================

  useEffect(() => {
    workflowStore.setIsNavigationDisabled(isNavigationDisabled);
  }, [workflowStore, isNavigationDisabled]);

  useEffect(() => {
    workflowStore.registerNodeClickGuard(handleNodeClick);
    return () => workflowStore.registerNodeClickGuard(null);
  }, [workflowStore, handleNodeClick]);

  useEffect(() => {
    workflowStore.registerExitGuard(handleExit);
    return () => workflowStore.registerExitGuard(null);
  }, [workflowStore, handleExit]);

  /**
   * Classification validation only runs when the domain is editable and the
   * user has real files (not "no evidence" / N/A).
   */
  const isClassificationValidationActive = isDomainInProgress && isApplicable && !noEvidenceChecked;
  const isAnyClassificationFailed = classificationIssues.some((i) => i.reason === 'failed');

  /**
   * Single source of truth for "is Save blocked?" — see workflow-selectors.ts
   * for the precedence rules.
   */
  const saveBlockReason = useMemo(
    () =>
      getSaveBlockReason({
        files: filesArray,
        validationError,
        isClassificationValidationActive,
        isAnyClassifying,
        isAnyClassificationFailed,
      }),
    [
      filesArray,
      validationError,
      isClassificationValidationActive,
      isAnyClassifying,
      isAnyClassificationFailed,
    ],
  );
  const isSaveBlockedByClassification =
    saveBlockReason === 'classifying' ||
    saveBlockReason === 'classification-failed' ||
    saveBlockReason === 'no-valid-doc';

  /**
   * Count of uploaded documents — used for the "No evidence" validation.
   */
  const uploadedDocumentsCount = useMemo(() => getUploadedDocumentsCount(filesArray), [filesArray]);

  /**
   * Check if all domains are completed
   * Used for enabling the Finish Assessment button
   */
  const allDomainsCompleted = useMemo(() => {
    const domains = assessment?.assessmentDomains;
    if (!domains || domains.length === 0) return false;
    return domains.every((domain) => domain.status === AssessmentDomainStatus.COMPLETED);
  }, [assessment?.assessmentDomains]);

  /**
   * Get next domain status
   * Used to control Next button visibility
   */
  const nextDomainStatus = useMemo(() => {
    if (!assessment) return undefined;

    const nextIndex = selectedIndex + 1;
    const nextDomain = findDomainByOrderIndex(assessment, nextIndex);

    return nextDomain?.status;
  }, [assessment, selectedIndex]);

  // Publish derived domain flags so DomainNavigation (and other observers)
  // can read them without prop-drilling.
  useEffect(() => {
    workflowStore.setCurrentDomainFlags({
      isDomainInProgress,
      isDomainCompleted: isCurrentDomainCompleted,
      isDomainFieldsDisabled,
      isFirstDomain: selectedIndex === 1,
      isLastDomain: progressFlowViewModel
        ? selectedIndex === progressFlowViewModel.totalDomains
        : false,
      allDomainsCompleted,
      uploadedDocumentsCount,
      isSaveAndNextDisabled: isSaveBlockedByClassification,
      isCurrentDomainRequired: domainDetailsViewModel?.isRequired ?? true,
      currentDomainStatus: domainDetailsViewModel?.status,
      nextDomainStatus,
    });
    return () => workflowStore.resetCurrentDomainFlags();
  }, [
    workflowStore,
    isDomainInProgress,
    isCurrentDomainCompleted,
    isDomainFieldsDisabled,
    selectedIndex,
    progressFlowViewModel,
    allDomainsCompleted,
    uploadedDocumentsCount,
    isSaveBlockedByClassification,
    domainDetailsViewModel?.isRequired,
    domainDetailsViewModel?.status,
    nextDomainStatus,
  ]);

  /**
   * Render the per-card change badge. The callback accepts the generic
   * FileUploadType (the upload component is shared) but every file rendered
   * inside this page is actually an AssessmentDocumentViewModel — cast to
   * read `changeType` without polluting the generic type.
   */
  const renderFileBadge = useCallback(
    (file: FileUploadType) => {
      const doc = file as AssessmentDocumentViewModel;
      const meta = doc.classificationMetadata;
      const isCompletedMatch =
        meta?.classificationStatus === DocumentClassificationStatus.COMPLETED &&
        !!meta.matchedStandardDocumentName;
      // A non-standard format still matched, so it earns the same badge slot as
      // a clean match — just amber instead of green. Any other invalid reason
      // means no match to badge at all.
      const isNonStandardFormat =
        meta?.invalidReason === DocumentClassificationInvalidReason.WRONG_FORMAT;
      const isValidMatch = isCompletedMatch && !meta?.invalidReason;

      return (
        <>
          <AssessmentDocumentChangeBadge
            changeType={doc.changeType}
            parentVersion={parentVersion}
          />
          {isValidMatch && (
            <TooltipCard
              variant="dark"
              description={t('app.assessment.classification.matchedBadge')}
            >
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle size={18} weight="fill" color="#16a34a" />
              </Box>
            </TooltipCard>
          )}
          {isCompletedMatch && isNonStandardFormat && (
            <TooltipCard
              variant="dark"
              description={t('app.assessment.classification.nonStandardFormat.title')}
            >
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                <WarningCircle size={18} weight="fill" color="#F59E0B" />
              </Box>
            </TooltipCard>
          )}
        </>
      );
    },
    [parentVersion, t],
  );

  // ============================================================================
  // RENDER: LOADING STATE
  // ============================================================================

  if (isLoading) {
    return <DomainDetailsSkeleton />;
  }

  // ============================================================================
  // RENDER: ERROR STATE
  // ============================================================================

  if (!domainDetailsViewModel) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h6" color="text.primary">
          <Trans i18nKey={'app.assessments.error.domainNotFound'} defaults="Domain not found" />
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <Trans
            i18nKey={'app.assessments.error.domainNotFoundDescription'}
            defaults="The requested domain could not be loaded. Please try again or contact support."
          />
        </Typography>
      </Box>
    );
  }

  // ============================================================================
  // RENDER: MAIN UI
  // ============================================================================

  return (
    <>
      {/* The three workflow modals (SaveChanges / ClassificationWarning /
          ExitAssessment) are rendered by AssessmentLayout as `<WorkflowModals />`.
          They read their open flags + handlers from AssessmentWorkflowStore,
          which the hooks publish into via register*ModalHandlers. */}

      {/* Domain Details Section — the surrounding scroll container, header,
          re-assessment banner and progress stepper live in AssessmentLayout. */}
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2, md: 3 },
            // Gray out and disable interactions when domain is not in progress
            ...((!isDomainInProgress || isMutating) && {
              opacity: 0.6,
            }),
          }}
        >
          {domainProfileNeeds && (
            <DomainFieldNeedsBanner
              needs={domainProfileNeeds}
              onGoToHub={() => navigate('/tenant-profile')}
            />
          )}

          <DomainDetails
            isRequired={domainDetailsViewModel?.isRequired}
            domainName={domainDetailsViewModel?.domainName}
            description={domainDetailsViewModel?.description}
            fields={domainDetailsViewModel?.fields}
            expectedDocuments={expectedDocuments}
            files={filesArray}
            onAddFiles={handleAddFiles}
            onDeleteFile={handleDeleteFile}
            onCancelUpload={handleCancelUpload}
            onUndoDelete={handleUndoDelete}
            onRetryDelete={handleRetryDelete}
            onRetryUpload={handleRetryUpload}
            onRemoveFailedUpload={handleRemoveFailedUpload}
            disabled={isDomainFieldsDisabled}
            isApplicable={isApplicable}
            onApplicabilityChange={setIsApplicable}
            domainStatus={domainDetailsViewModel?.status}
            noEvidenceChecked={noEvidenceChecked}
            renderExtraContent={renderClassificationBadge}
            renderFileBadge={renderFileBadge}
            isFileMoving={(file) => isDocumentMoving(file.id)}
            isFileProcessing={(file) => isDocumentProcessing(file.id)}
            parentVersion={parentVersion}
            preloadedDocumentCount={preloadedDocumentCount}
            parentDomainScorePercentage={parentDomainScorePercentage}
            onDisabledAreaClick={editPromptTooltip.show}
          />
        </Box>

        {/* Navigation Section — pinned to the bottom of the scroll viewport so
            the domain controls stay reachable without scrolling to the end of
            a long domain. It stays in flow (sticky), so it never eats into the
            domain content's space. */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            px: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 1, md: 1.25 },
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'grey.200',
            borderBottomLeftRadius: 'inherit',
            borderBottomRightRadius: 'inherit',
            boxShadow: '0 -4px 12px rgba(16, 24, 40, 0.06)',
            flexShrink: 0,
          }}
        >
          <DomainValidationBanner state={saveBlockReason} validationError={validationError} />
          {/* DomainNavigation reads its derived flags (isLastDomain,
              isDomainInProgress, uploadedDocumentsCount, ...) directly from
              AssessmentWorkflowStore — see the `setCurrentDomainFlags` effect
              above. Only handlers + controlled state are passed as props. */}
          <DomainNavigation
            onBack={handleBack}
            onResume={handleResume}
            onSaveAndNext={handleSaveWithClassificationCheck}
            onNext={handleNext}
            onResumeLater={handleResumeLater}
            onEdit={handleEditDomain}
            onGoToSummary={() => navigate(routes.assessmentSummary(assessmentId))}
            onNoEvidenceChange={setNoEvidenceChecked}
            noEvidenceChecked={noEvidenceChecked}
            isApplicable={isApplicable}
            editPromptTooltipOpen={editPromptTooltip.isOpen}
            onEditPromptTooltipOpen={editPromptTooltip.show}
            onEditPromptTooltipClose={editPromptTooltip.hide}
          />
        </Box>
      </Paper>
    </>
  );
});
